import { MessageSquare, X } from 'lucide-react'
import type { Comment } from '../../hooks/useComments'
import CommentThread from './CommentThread'

interface Props {
  comments: Comment[]
  isOpen: boolean
  onToggle: () => void
  activeCommentId: string | null
  detachedIds: Set<string>
  onReply: (commentId: string, body: string) => Promise<Comment | null>
  onResolve: (commentId: string) => void
  onUnresolve: (commentId: string) => void
  onDelete: (commentId: string) => void
  onThreadClick: (commentId: string) => void
}

export default function CommentSidebar({
  comments,
  isOpen,
  onToggle,
  activeCommentId,
  detachedIds,
  onReply,
  onResolve,
  onUnresolve,
  onDelete,
  onThreadClick,
}: Props) {
  const unresolvedCount = comments.filter(c => !c.resolved).length

  // Sort: anchored first, detached at bottom
  const sorted = [...comments].sort((a, b) => {
    const aDetached = detachedIds.has(a.id) ? 1 : 0
    const bDetached = detachedIds.has(b.id) ? 1 : 0
    if (aDetached !== bDetached) return aDetached - bDetached
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  })

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="fixed right-4 top-20 z-30 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm hover:bg-gray-50 transition-colors"
      >
        <MessageSquare className="h-4 w-4" />
        {unresolvedCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-medium text-white">
            {unresolvedCount}
          </span>
        )}
      </button>

      {/* Sidebar panel */}
      {isOpen && (
        <div className="fixed right-0 top-0 z-40 h-screen w-80 border-l border-gray-200 bg-white shadow-lg flex flex-col">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Comments</h3>
              {unresolvedCount > 0 && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                  {unresolvedCount}
                </span>
              )}
            </div>
            <button
              onClick={onToggle}
              className="rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {sorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <MessageSquare className="h-8 w-8 mb-2" />
                <p className="text-sm">No comments yet</p>
                <p className="text-xs mt-1">Select text and click the comment button</p>
              </div>
            ) : (
              sorted.map(comment => (
                <CommentThread
                  key={comment.id}
                  comment={comment}
                  isDetached={detachedIds.has(comment.id)}
                  isActive={activeCommentId === comment.id}
                  onReply={onReply}
                  onResolve={onResolve}
                  onUnresolve={onUnresolve}
                  onDelete={onDelete}
                  onClick={() => onThreadClick(comment.id)}
                />
              ))
            )}
          </div>
        </div>
      )}
    </>
  )
}
